import {useCallback, useEffect, useMemo} from "react";
import {useDispatch} from "react-redux";
import {useSearchParams} from "react-router-dom";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {
    endOfDay,
    startOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import {
    useCurrentApp,
    useCurrentAppId,
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useAllRootResourcesForSector} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {resetSearchBar} from "../../../../store/action.reducer";
import {ReservationActions} from "../../../../store/reservations.reducer";
import type {DatePickerChangeReason} from "../../../common/newDatePicker/datePickerShared";
import {
    FLOORPLAN_QUERY_PARAMS,
    type FloorplanDateSelection,
    floorplanDateParam,
    floorplanDateSelection,
    floorplanSearchParamsForRestaurant,
    floorplanStorageKeys,
    isFloorplanDaySlice,
    isFloorplanTime,
    parseFloorplanDateParam,
} from "../floorplanState";
import {persistSectorDate, readSectorDate} from "../../sectorDate";

export interface FloorplanViewState {
    date: Date;
    navigateToDate: (date: Date, reason?: DatePickerChangeReason) => void;
    restaurant?: Resource;
    restaurants: Resource[];
    selectRestaurant: (restaurantId: string) => void;
}

export function useFloorplanViewState(explicitSector?: Sector): FloorplanViewState {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const appId = useCurrentAppId();
    const appData = useCurrentApp();
    const configuredSector = useSector(appData?.configuration?.sectorId);
    const sector = explicitSector ?? configuredSector;
    const sectorId = sector?.id;
    const restaurants = useAllRootResourcesForSector(sectorId);
    const restaurantSessionKey = `floorplanRestaurant-${appId}-${sectorId}`;
    const restaurantIdFromUrl = searchParams.get(FLOORPLAN_QUERY_PARAMS.restaurantId);
    const dateParam = searchParams.get(FLOORPLAN_QUERY_PARAMS.date);
    const dayPartParam = searchParams.get(FLOORPLAN_QUERY_PARAMS.dayPart);
    const timeStartParam = searchParams.get(FLOORPLAN_QUERY_PARAMS.timeStart);
    const timeEndParam = searchParams.get(FLOORPLAN_QUERY_PARAMS.timeEnd);
    const restaurant = useMemo(() => {
        const urlRestaurant = restaurantIdFromUrl
            ? restaurants.find(resource => resource.id === restaurantIdFromUrl)
            : undefined;
        if (urlRestaurant) return urlRestaurant;

        const storedRestaurantId = sessionStorage.getItem(restaurantSessionKey);
        const storedRestaurant = storedRestaurantId
            ? restaurants.find(resource => resource.id === storedRestaurantId)
            : undefined;
        if (storedRestaurant) return storedRestaurant;

        const persistedRestaurantId = localStorage.getItem(restaurantSessionKey);
        const persistedRestaurant = persistedRestaurantId
            ? restaurants.find(resource => resource.id === persistedRestaurantId)
            : undefined;
        if (persistedRestaurant) return persistedRestaurant;

        return restaurants.find(resource => resource.meta?.preferred === true) ?? restaurants[0];
    }, [restaurantIdFromUrl, restaurantSessionKey, restaurants]);
    const restaurantKeys = useMemo(
        () => restaurant?.id ? floorplanStorageKeys(restaurant.id) : undefined,
        [restaurant?.id],
    );
    // A deep link wins, then the day the sector's other tabs are on, then today.
    const dateSelection = useMemo<FloorplanDateSelection>(() => (
        parseFloorplanDateParam(dateParam)
        ?? readSectorDate(sectorId)
        ?? {date: startOfDay(new Date()), mode: "today"}
    ), [dateParam, sectorId]);
    const date = dateSelection.date;
    const reservationDaySlice = useMemo(() => {
        const restaurantValue = restaurantKeys ? localStorage.getItem(restaurantKeys.dayPart) : null;
        const legacyValue = sectorId ? localStorage.getItem(`reservationDaySlice-${sectorId}`) : null;
        return isFloorplanDaySlice(dayPartParam)
            ? dayPartParam
            : isFloorplanDaySlice(restaurantValue)
                ? restaurantValue
                : isFloorplanDaySlice(legacyValue)
                    ? legacyValue
                    : "now";
    }, [dayPartParam, restaurantKeys, sectorId]);
    const startTimeFrame = useMemo(() => {
        const restaurantValue = restaurantKeys ? localStorage.getItem(restaurantKeys.timeStart) : null;
        const legacyValue = sectorId ? localStorage.getItem(`startTimeFrame-${sectorId}`) : null;
        return isFloorplanTime(timeStartParam)
            ? timeStartParam
            : isFloorplanTime(restaurantValue)
                ? restaurantValue
                : isFloorplanTime(legacyValue)
                    ? legacyValue
                    : "00:00";
    }, [restaurantKeys, sectorId, timeStartParam]);
    const endTimeFrame = useMemo(() => {
        const restaurantValue = restaurantKeys ? localStorage.getItem(restaurantKeys.timeEnd) : null;
        const legacyValue = sectorId ? localStorage.getItem(`endTimeFrame-${sectorId}`) : null;
        return isFloorplanTime(timeEndParam)
            ? timeEndParam
            : isFloorplanTime(restaurantValue)
                ? restaurantValue
                : isFloorplanTime(legacyValue)
                    ? legacyValue
                    : "23:59";
    }, [restaurantKeys, sectorId, timeEndParam]);

    const navigateToDate = useCallback((
        nextDate: Date,
        reason: DatePickerChangeReason = "selection",
    ) => {
        if (Number.isNaN(nextDate.getTime())) return;

        const nextSelection = floorplanDateSelection(nextDate, reason);

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {
                fromDate: nextSelection.date.toISOString(),
                toDate: endOfDay(nextSelection.date).toISOString(),
            },
        });
        persistSectorDate(sectorId, nextSelection);
        setSearchParams(currentSearchParams => {
            const nextSearchParams = new URLSearchParams(currentSearchParams);
            nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.date, floorplanDateParam(nextSelection));
            return nextSearchParams;
        }, {replace: true});
    }, [appId, dispatch, sectorId, setSearchParams]);

    const selectRestaurant = useCallback((restaurantId: string) => {
        const restaurant = restaurants.find(resource => resource.id === restaurantId);
        if (!restaurant) return;

        sessionStorage.setItem(restaurantSessionKey, restaurantId);
        localStorage.setItem(restaurantSessionKey, restaurantId);
        setSearchParams(
            currentSearchParams => floorplanSearchParamsForRestaurant(currentSearchParams, restaurantId),
            {replace: true},
        );
    }, [restaurantSessionKey, restaurants, setSearchParams]);

    useEffect(() => {
        if (!restaurant?.id) return;

        sessionStorage.setItem(restaurantSessionKey, restaurant.id);
        localStorage.setItem(restaurantSessionKey, restaurant.id);
        persistSectorDate(sectorId, dateSelection);
        if (restaurantKeys) {
            localStorage.setItem(restaurantKeys.dayPart, reservationDaySlice);
            localStorage.setItem(restaurantKeys.timeStart, startTimeFrame);
            localStorage.setItem(restaurantKeys.timeEnd, endTimeFrame);
        }

        const canonicalDateParam = floorplanDateParam(dateSelection);
        if (
            restaurantIdFromUrl !== restaurant.id
            || dateParam !== canonicalDateParam
            || dayPartParam !== reservationDaySlice
            || timeStartParam !== startTimeFrame
            || timeEndParam !== endTimeFrame
        ) {
            setSearchParams(currentSearchParams => {
                const nextSearchParams = new URLSearchParams(currentSearchParams);
                nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.restaurantId, restaurant.id!);
                nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.date, canonicalDateParam);
                nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.dayPart, reservationDaySlice);
                nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.timeStart, startTimeFrame);
                nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.timeEnd, endTimeFrame);
                return nextSearchParams;
            }, {replace: true});
        }

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {
                activeTab: "floorplan",
                selectedResourceSingle: restaurant,
                fromDate: dateSelection.date.toISOString(),
                toDate: endOfDay(dateSelection.date).toISOString(),
                reservationDaySlice,
                startTimeFrame,
                endTimeFrame,
            },
        });
    }, [
        appId,
        dateParam,
        dateSelection,
        dayPartParam,
        dispatch,
        endTimeFrame,
        restaurant,
        restaurantIdFromUrl,
        restaurantKeys,
        restaurantSessionKey,
        reservationDaySlice,
        sectorId,
        setSearchParams,
        startTimeFrame,
        timeEndParam,
        timeStartParam,
    ]);

    useEffect(() => {
        dispatch(resetSearchBar());
    }, [dispatch]);

    return {
        date,
        navigateToDate,
        restaurant,
        restaurants,
        selectRestaurant,
    };
}
